<script setup lang="ts">
import {
  Folder, FolderOpen, FileText, ChevronRight,
  MoreVertical, LoaderCircle, AlertCircle,
  ExternalLink, Briefcase, Building2, Table2, MessagesSquare,
} from 'lucide-vue-next'
import { formatRelativeTimeCompact } from '~/utils/relativeTime'
import { useDateHelper } from '~/composables/useDateHelper'
import { fileExt, extBadgeClass } from '~/lib/fileType'
import { storeToRefs } from 'pinia'
import type { ItemRow } from '~~/shared/items/itemRow'
import { isItemIndexed } from '~~/shared/items/itemPending'
import { useDocumentsStore } from '~/stores/documents'
import LoadFailureNotice from '~/components/documents/LoadFailureNotice.vue'
import { useDocumentsIndexingStore } from '~/stores/documentsIndexing'
import { useFolderDrop } from '~/composables/useFolderDrop'
import DocumentStatusChip from '~/components/documents/DocumentStatusChip.vue'
import { isUnsettled } from '~/lib/documentStatus'
import { useDocumentHomes } from '~/composables/useDocumentHomes'
import { DOCUMENT_COLUMNS, columnVisibility } from '~/components/documents/documentColumns'

/**
 * ── ONE ROW COMPONENT, TWO VARIANTS (AlexAmin/marcus#169) ───────────────────────────────────────
 *
 * `list` is the row this component has always rendered — badge, name, one meta cell — and it is what
 * the SIDEBAR RAIL gets, unchanged. `table` splits the meta half into the columns
 * `documentColumns.ts` declares, and is what `/app/docs` gets.
 *
 * **Only the row's inner markup branches.** Selection, range-select, drag, drop-onto-folder,
 * expand/collapse, the nested-children recursion and the context menu are ~150 lines that both
 * variants share and neither forks. A second component would fork them, and forking is precisely how
 * the two toolbars drifted apart before AlexAmin/marcus#158 — a reviewer who finds a second row
 * component here should treat it as the bug.
 *
 * The rail cannot take a table: `PanelLayout` gives it `sm:w-72` (288px), resizable down to 200 by
 * `ResizeHandle :min-size="200"`, and five columns do not fit in 200px. It passes no `variant`, so it
 * keeps the list. That is the whole of the rail's story under this design — no header row, no column
 * cells, no controls, the same band above it.
 */
const props = withDefaults(defineProps<{
  items: ItemRow[]
  variant?: 'list' | 'table'
  /**
   * The band this list sits under, when it sits under one. Used by the `Otros lugares` column to
   * leave out the home the header directly above the row has already stated — restating it costs the
   * width and says nothing.
   */
  groupId?: string | null
}>(), { variant: 'list', groupId: null })

const isTable = computed(() => props.variant === 'table')
const { homesFor } = useDocumentHomes()

/** Column geometry, read from the one definition the header reads, so a cell and its header cannot
 *  disagree about width or about when the column disappears. */
function columnClass(key: string): string {
  const column = DOCUMENT_COLUMNS.find(c => c.key === key)
  return column ? `${column.width} ${columnVisibility(column)}` : ''
}

/**
 * Width only, no display class — for the NAME cell, which is a `<span>` carrying `truncate`.
 * `display: flex` on it would make the text an anonymous flex item and `text-overflow` would stop
 * applying, so a long file name would overflow its column instead of ellipsizing. Name is the one
 * column that never hides, so it needs no visibility class either.
 */
function columnWidth(key: string): string {
  return DOCUMENT_COLUMNS.find(c => c.key === key)?.width ?? ''
}

/**
 * The settled status word. `DocumentStatusChip` still owns every UNSETTLED state — processing,
 * error, quota_blocked, uploading — because those are conditions rather than values, and `basura`
 * sitting in Error has to keep reading as broken. This is only the three-value lens the Estado
 * column filters by.
 */
const STATUS_LABELS: Record<string, string> = {
  draft: 'documents.statusDraft',
  reviewed: 'documents.statusReviewed',
  final: 'documents.statusFinal',
}

/** The same four icons `DocumentSourceFilter` uses for the same four kinds of home, so a chip in the
 *  column and the source it names read as one thing. */
const HOME_ICONS = { client: Building2, case: Briefcase, sheet: Table2, conversation: MessagesSquare }


const { t, locale } = useI18n()
const { formatDateTimeForDisplay } = useDateHelper()
const route = useRoute()
const indexing = useDocumentsIndexingStore()
const documents = useDocumentsStore()
const { selectedIds, dragItemId, dropTargetId } = storeToRefs(documents)
const {
  selectOnly, toggleSelected, selectRange, openFolder,
  toggleExpand, isExpanded, isChildrenLoading, getChildren,
  startDrag, endDrag,
  // AlexAmin/marcus#469 — whether THIS folder's children answered. Keyed by folder, so one broken
  // expansion cannot accuse its siblings.
  isChildrenFailed, fetchChildren,
} = documents


const openDocId = computed(() => {
  const match = route.path.match(/^\/app\/docs\/(.+)$/)
  return match ? match[1] : null
})

const emit = defineEmits<{
  contextmenu: [e: MouseEvent, item: ItemRow]
}>()

// Drop-onto-folder behaviour is shared with BrowserItemGrid via useFolderDrop, so both surfaces
// enforce the same upload/move rules and guards.
const { onFolderDragOver: onRowDragOver, onFolderDragLeave: onRowDragLeave, onFolderDrop: onRowDrop } = useFolderDrop()

// Plain click = select only. Cmd/Ctrl = toggle, Shift = range. Expansion is
// driven exclusively by the chevron; opening is double-click (Finder-style).
function handleClick(item: ItemRow, e?: MouseEvent) {
  if (item.status === 'uploading') return
  if (e?.metaKey || e?.ctrlKey) {
    toggleSelected(item.id)
  } else if (e?.shiftKey) {
    selectRange(item.id)
  } else {
    selectOnly(item.id)
  }
}

// The disclosure triangle toggles expansion without affecting selection.
function toggleExpandClick(item: ItemRow) {
  if (item.status === 'uploading' || item.type !== 'folder') return
  toggleExpand(item)
}

function openItem(item: ItemRow) {
  if (item.status === 'uploading') return
  if (item.type === 'folder') openFolder(item)
  else navigateTo(`/app/docs/${item.id}`)
}

function handleDblClick(item: ItemRow) {
  openItem(item)
}

function openExternal(url: string) {
  window.open(url, '_blank')
}
</script>

<template>
  <div
    v-for="item in items"
    :key="item.id"
  >
    <!-- Item row -->
    <div
      class="group flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring"
      role="treeitem"
      :tabindex="selectedIds.has(item.id) ? 0 : -1"
      :aria-selected="selectedIds.has(item.id)"
      :aria-expanded="item.type === 'folder' ? isExpanded(item.id) : undefined"
      :class="[
        dropTargetId === item.id
          ? 'bg-primary-light ring-1 ring-inset ring-primary/50'
          : (selectedIds.has(item.id) || openDocId === item.id) ? 'bg-accent-surface' : 'hover:bg-accent-lighter',
      ]"
      draggable="true"
      @click="handleClick(item, $event)"
      @dblclick="handleDblClick(item)"
      @contextmenu="emit('contextmenu', $event, item)"
      @dragstart="startDrag(item, $event)"
      @dragend="endDrag"
      @dragover="onRowDragOver($event, item)"
      @dragleave="onRowDragLeave"
      @drop="onRowDrop($event, item)"
    >
      <!-- Checkbox -->
      <div
        class="h-4 w-4 shrink-0 flex items-center justify-center"
        @click.stop="item.status !== 'uploading' && toggleSelected(item.id)"
      >
        <input
          v-if="item.status !== 'uploading'"
          type="checkbox"
          :checked="selectedIds.has(item.id)"
          class="h-3.5 w-3.5 rounded border-muted-foreground/30 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer pointer-events-none"
          :class="selectedIds.has(item.id) && '!opacity-100'"
        />
      </div>

      <!-- Icon -->
      <template v-if="item.type === 'folder'">
        <button
          type="button"
          class="h-4 w-4 -mx-0.5 flex items-center justify-center shrink-0 rounded-md hover:bg-accent-lighter"
          :aria-label="isExpanded(item.id) ? t('common.collapse') : t('common.expand')"
          @click.stop="toggleExpandClick(item)"
          @dblclick.stop
        >
          <ChevronRight
            class="h-3.5 w-3.5 text-muted-foreground transition-transform duration-150"
            :class="isExpanded(item.id) && 'rotate-90'"
          />
        </button>
        <component :is="isExpanded(item.id) ? FolderOpen : Folder" class="h-4 w-4 text-warning-ink shrink-0" />
      </template>
      <template v-else>
        <span
          class="inline-flex items-center justify-center h-5 w-8 text-[9px] font-bold rounded-md shrink-0"
          :class="extBadgeClass(fileExt(item.file_name))"
        >
          {{ fileExt(item.file_name) }}
        </span>
      </template>

      <!-- Name — the one cell both variants share, so the geometry of the left half is identical -->
      <span
        class="text-xs truncate"
        :class="[
          isTable ? columnWidth('name') : 'flex-1',
          !isItemIndexed(item.status) && 'text-muted-foreground',
        ]"
      >{{ item.name }}</span>

      <!--
        ── THE TABLE VARIANT'S CELLS ──────────────────────────────────────────────────────────────
        Four columns, in the order `documentColumns.ts` declares them, each taking its width from the
        same definition the header row reads. A folder renders its child count in Tipo and nothing
        else: it has no extension, no status and no other home, and inventing a dash for each would
        be three cells of noise on the rows people are navigating through.
      -->
      <template v-if="isTable">
        <div class="items-center text-[10px] text-muted-foreground" :class="columnClass('type')">
          <span v-if="item.type === 'folder'">{{ item.child_count ?? 0 }}</span>
          <span v-else class="uppercase">{{ fileExt(item.file_name) }}</span>
        </div>
        <div class="items-center" :class="columnClass('status')">
          <!-- Unsettled states keep the chip they have everywhere else — a condition, not a value. -->
          <DocumentStatusChip v-if="item.type !== 'folder' && isUnsettled(item)" :item="item" />
          <span
            v-else-if="item.type !== 'folder' && item.status && STATUS_LABELS[item.status]"
            class="truncate text-[10px] text-muted-foreground"
          >{{ t(STATUS_LABELS[item.status]!) }}</span>
        </div>
        <!--
          Otros lugares — the column this table exists for. Where else this document lives, minus the
          band it is already under. Empty is a real and common value and renders as NOTHING, never as
          a dash: a dash reads as "we looked and there is none", and on a document past the org
          listing's per-group cap that would be a claim we cannot make (see `useDocumentHomes`).
        -->
        <div class="min-w-0 items-center gap-1 overflow-hidden" :class="columnClass('homes')">
          <span
            v-for="home in homesFor(item.id, props.groupId)"
            :key="home.kind + home.id"
            class="inline-flex min-w-0 max-w-full items-center gap-0.5 rounded bg-muted px-1 text-[10px] text-muted-foreground"
            :title="home.name"
          >
            <component :is="HOME_ICONS[home.kind]" class="h-2.5 w-2.5 shrink-0" />
            <span class="truncate">{{ home.name }}</span>
          </span>
        </div>
        <!--
          The EXACT timestamp is the title, and it is not decoration: it is what separates the fifth
          and the sixth `Convenio` when both read `hace 27 d`.
        -->
        <div
          class="items-center justify-end whitespace-nowrap text-[10px] text-muted-foreground"
          :class="columnClass('modified')"
          :title="formatDateTimeForDisplay(item.updated_at)"
        >
          {{ formatRelativeTimeCompact(item.updated_at, locale) }}
        </div>
      </template>

      <!-- Meta (list variant) -->
      <template v-else-if="item.type === 'folder'">
        <span class="text-[10px] text-muted-foreground whitespace-nowrap">
          {{ item.child_count ?? 0 }}
        </span>
      </template>
      <template v-else-if="!isTable">
        <!-- Shared with every other document listing (`DocumentStatusChip`). This block was the only
             place in the app that showed whether a document was actually usable, which is why the
             case hub and the client page could show a still-processing file as ready. Its uploading
             label was also hardcoded Spanish. -->
        <DocumentStatusChip v-if="isUnsettled(item)" :item="item" />
        <template v-else>
          <span
            v-if="item.source_backend === 'google_drive' && item.google_drive_url"
            class="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground hover:text-primary cursor-pointer whitespace-nowrap"
            :title="t('browser.openInGoogleDocs')"
            @click.stop="openExternal(item.google_drive_url!)"
          >
            <ExternalLink class="h-3 w-3" />
          </span>
          <span
            v-else-if="item.source_backend === 'sharepoint' && item.onedrive_url"
            class="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground hover:text-primary cursor-pointer whitespace-nowrap"
            :title="t('browser.openInWordOnline')"
            @click.stop="openExternal(item.onedrive_url!)"
          >
            <ExternalLink class="h-3 w-3" />
          </span>
          <!-- Indexed during this session: the row went from "Procesando" to usable while the user
               was watching, so say which one just landed. Clears with the tray. -->
          <span
            v-if="indexing.isRecentlyIndexed(item.id)"
            class="ml-auto text-[10px] font-medium text-info whitespace-nowrap"
            :title="t('indexing.newlyIndexedTitle')"
          >
            {{ t('indexing.newlyIndexed') }}
          </span>
          <span v-else class="ml-auto text-[10px] text-muted-foreground whitespace-nowrap">
            {{ formatRelativeTimeCompact(item.updated_at, locale) }}
          </span>
        </template>
      </template>

      <!-- Kebab menu -->
      <button
        v-if="item.status !== 'uploading'"
        type="button"
        class="h-5 w-5 flex items-center justify-center rounded-md text-muted-foreground opacity-100 md:opacity-0 md:group-hover:opacity-100 hover:bg-accent-lighter transition-all shrink-0"
        @click.stop="emit('contextmenu', $event, item)"
      >
        <MoreVertical class="h-3.5 w-3.5" />
      </button>
    </div>

    <!-- Children (nested, recursive) -->
    <div v-if="item.type === 'folder' && isExpanded(item.id)" class="pl-5 border-l border-border ml-5">
      <BrowserItemList
        :items="getChildren(item.id)"
        :variant="props.variant"
        :group-id="props.groupId"
        @contextmenu="(e: MouseEvent, i: ItemRow) => emit('contextmenu', e, i)"
      />
      <div
        v-if="isChildrenLoading(item.id)"
        class="flex items-center gap-1.5 px-3 py-2 text-[10px] text-muted-foreground"
      >
        <LoaderCircle class="h-3 w-3 animate-spin" />
        {{ t('common.loading') }}
      </div>
      <!--
        THE SENTENCE THE SPINNER ABOVE EXISTS TO PREVENT (AlexAmin/marcus#469).

        The loading spinner was added precisely so an expanding folder never flashes
        `browser.emptyFolder` before its children arrive. A FAILED fetch walked straight past it:
        `fetchChildren` ended in a bare `catch {}` under a `// tracked by useApi` comment that is
        false for a 500, `childIds` got no entry, the spinner cleared, and the row settled on
        "Carpeta vacía" — the exact claim the spinner was protecting against, now permanent.

        `inline`, because this sits inside one row's expanded children: same words and same
        `role="alert"`, sized so a nested folder does not get a full-panel empty state under it.
      -->
      <LoadFailureNotice
        v-else-if="isChildrenFailed(item.id)"
        variant="inline"
        :message="t('documents.childrenFailed')"
        @retry="fetchChildren(item.id)"
      />

      <div
        v-else-if="getChildren(item.id).length === 0"
        class="px-3 py-2 text-[10px] text-muted-foreground"
      >
        {{ t('browser.emptyFolder') }}
      </div>
    </div>
  </div>
</template>
